/**
 * 画面詳細の右パネル。コンポーネントの表の行を押すと開き、表は見えたままにする（非モーダル）。
 *
 * **右パネルがコンポーネントの詳細の置き場である。** 画面仕様書として要る項目を畳まずに全部並べる
 * （Dhik 2026-09-29「一般的に画面仕様書として必要な情報はだしてくれ」「表だとゴチャつくから押下した
 * ときのサイドバーの情報といい感じに分散させてほしい」）。**項目名は見本の画面仕様書の列名をそのまま
 * 使い、言い換えを作らない**（同日「謎日本語使うな」）。
 *
 * | 見本の列 | 右パネルの項目の中身 |
 * |---|---|
 * | 種別 | 種別 |
 * | 表示条件 | 判定表の業務ルール（リンク）。無ければ「常に表示」 |
 * | 変数部の表示ロジック | 名前の材料の用語（用語詳細へのリンク）と定義の 1 行目 |
 * | 操作結果 | 「「〈業務名〉」を実行する」（業務 ・ 業務ルールへのリンク）と「「〈画面名〉」に遷移する」（画面へのリンク） |
 *
 * 初期表示・変数部・バリデーションの文言は仕様グラフが持たない（判定は処理が、エラーの文言は画面仕様書
 * が持つ）ので、変数部の表示ロジックは用語の名前と定義で答え、初期表示 ・ バリデーション ・ Note は出さない。
 *
 * **値が無い項目は「—」にして項目ごと残す。** 項目を消すと、「無い」のか「描き忘れた」のかが
 * 右パネルから読めない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面詳細の右パネルで、コンポーネント 1 件の種別・表示条件・変数部の表示ロジック・操作結果を畳まずに描き、見出しのコンポーネント名の用語と本文の用語を用語詳細へ繋ぐ。案件モードでは変更前と変更後を 2 列に並べる。
 * @graph-connects none
 */
import type { ScreenComponentRow } from '@cortex/domain-spec-console';
import { Link } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { TermText } from '../components/spec/TermText.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { pjLinkSearch } from './pj-link.js';
import {
  BUSINESS_DETAIL_ROUTE,
  RULE_ROUTE,
  SCREEN_DETAIL_ROUTE,
  TERM_DETAIL_ROUTE,
} from './route-paths.js';
import {
  ComponentName,
  componentKindText,
  DASH,
  operationResultsOf,
  type OperationResult,
  type ParamTranslate,
} from './screen-detail-cells.js';

/** 右パネルの項目の見出しの class。 @graph-connects none */
export const FIELD_HEAD_CLASS = 'mt-4 mb-1 text-xs font-bold text-[var(--color-text-secondary)]';

/** 右パネルの並びの 1 行の class。業務フロー図の右パネルも同じ見た目で使う。 @graph-connects none */
export const ITEM_CLASS = 'border-b border-[var(--color-border)] py-1.5 text-sm';

/** 右パネルのリンクの class。 @graph-connects none */
const LINK_CLASS = 'text-[var(--color-accent-primary)] underline';

/**
 * 右パネルの項目。見本の画面仕様書の列の順に並べる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 右パネルに並べる画面仕様書の項目（種別・表示条件・変数部の表示ロジック・操作結果）の順
 * @graph-connects none
 */
const PANEL_FIELDS = ['kind', 'visibleWhen', 'variableLogic', 'result'] as const;

/** 右パネルの項目の種類。 @graph-connects none */
type PanelFieldKey = (typeof PANEL_FIELDS)[number];

/**
 * 用語の定義の 1 行目。定義は用語の詳細で全文を読めるので、右パネルは 1 行に留める
 * （`split` の上限 1 で、改行が無ければ全文がそのまま残る）。
 * @graph-connects none
 */
function firstLineOf(text: string): string {
  return text.split('\n', 1).join('');
}

/**
 * コンポーネント 1 件の右パネルの項目を、項目ごとの文字にする（案件モードの 2 列で左右を比べる材料）。
 *
 * 値が無い項目は「—」、表示条件の無いコンポーネントは「常に表示」。変数部の表示ロジックは用語の
 * 名前と定義の 1 行目を、操作結果は行を改行で繋ぐ。
 *
 * @param component コンポーネント 1 件
 * @param translate 表示言語への翻訳
 * @returns 項目ごとの文字
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネント 1 件の種別・表示条件・変数部の表示ロジック・操作結果を、項目ごとの文字にする。
 * @graph-connects componentKindText [calls] 種別
 * @graph-connects operationResultsOf [calls] 操作結果の行
 * @graph-connects displayLabel [calls] 用語 ・ 業務ルールの表示名
 */
export function componentPanelLines(
  component: ScreenComponentRow,
  translate: ParamTranslate,
): Record<PanelFieldKey, string> {
  const subject = component.subject ?? null;
  const results = operationResultsOf(component, translate);
  return {
    kind: componentKindText(component, translate) ?? DASH,
    visibleWhen:
      component.visibleWhen === null
        ? translate('screenDetail.panel.alwaysVisible')
        : displayLabel(component.visibleWhen.label, translate),
    variableLogic:
      subject === null
        ? DASH
        : [
            displayLabel(subject.label, translate),
            ...(subject.description === null || subject.description === ''
              ? []
              : [firstLineOf(subject.description)]),
          ].join('\n'),
    result: results.length === 0 ? DASH : results.map((line) => line.text).join('\n'),
  };
}

/**
 * 操作結果 1 行のリンク。業務は業務詳細へ、業務ルールは業務ルールの画面へ、画面は画面詳細へ繋ぐ。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 操作結果 1 行を、実行する業務 ・ 業務ルールか遷移する画面へのリンクで描き、案件を引き継ぐ。
 * @graph-connects pjLinkSearch [calls] 案件を次の画面へ引き継ぐ検索条件
 * @graph-connects BUSINESS_DETAIL_ROUTE [references] 実行する業務の詳細
 * @graph-connects RULE_ROUTE [references] 実行する業務ルール
 * @graph-connects SCREEN_DETAIL_ROUTE [references] 遷移する画面の詳細
 */
function OperationResultLink({ result, pj }: { result: OperationResult; pj: string | undefined }) {
  const id = String(result.id);
  if (result.route === 'business') {
    return (
      <Link
        to={BUSINESS_DETAIL_ROUTE}
        params={{ businessId: id }}
        {...pjLinkSearch(pj)}
        className={LINK_CLASS}
      >
        {result.text}
      </Link>
    );
  }
  if (result.route === 'rule') {
    return (
      <Link to={RULE_ROUTE} params={{ ruleId: id }} {...pjLinkSearch(pj)} className={LINK_CLASS}>
        {result.text}
      </Link>
    );
  }
  return (
    <Link
      to={SCREEN_DETAIL_ROUTE}
      params={{ screenId: id }}
      {...pjLinkSearch(pj)}
      className={LINK_CLASS}
    >
      {result.text}
    </Link>
  );
}

/**
 * Esc で右パネルを閉じる。開いている間だけ耳を立てる。
 *
 * @param open 右パネルが開いているか
 * @param onClose 閉じる動き
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 右パネルが開いている間、Esc キーで閉じられるようにする。
 * @graph-connects none
 */
export function useEscapeToClose(open: boolean, onClose: () => void): void {
  useEffect(() => {
    if (!open) return undefined;
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [open, onClose]);
}

/** `PanelFrame` の props。 @graph-connects none */
export interface PanelFrameProps {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** 枠の読み上げ名。省けば画面詳細のコンポーネントの詳細。 */
  ariaLabel?: string;
  /** ✕ の読み上げ名。省けば画面詳細の「閉じる」。 */
  closeLabel?: string;
  /** 枠に付ける `data-*` 属性。省けば画面詳細の `data-screen-panel`。 */
  dataset?: Readonly<Record<`data-${string}`, string>>;
}

/**
 * 右パネルの枠。見出しと ✕（「閉じる」は指すと出す文字と読み上げの名前にだけ載せる）。画面詳細と
 * 受け入れ条件（業務詳細の v2）が同じ見た目で使うので、読み上げ名・✕ の名前・印の属性は差し替えられる。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 右パネルの枠を、見出しと ✕ の閉じるボタンつきで描く。
 * @graph-connects none
 */
export function PanelFrame({
  title,
  onClose,
  children,
  ariaLabel,
  closeLabel,
  dataset,
}: PanelFrameProps) {
  const { t } = useTranslation();
  const close = closeLabel ?? t('screenDetail.panel.close');
  return (
    <aside
      aria-label={ariaLabel ?? t('screenDetail.panel.label')}
      {...(dataset ?? { 'data-screen-panel': '' })}
      className="self-start rounded-sm border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 pt-3 pb-5 lg:sticky lg:top-0 lg:max-h-screen lg:overflow-auto"
    >
      <div className="flex items-start gap-3">
        <h2 className="flex-1 text-base leading-snug font-bold text-[var(--color-text-primary)]">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          title={close}
          aria-label={close}
          data-panel-close
          className="rounded-sm px-1.5 text-base leading-none text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]"
        >
          <span aria-hidden="true">✕</span>
        </button>
      </div>
      {children}
    </aside>
  );
}

/**
 * 右パネルの項目 1 つ（見出しと中身）。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 右パネルの項目 1 つを、見出しと中身で描く。
 * @graph-connects none
 */
function PanelItem({ field, children }: { field: PanelFieldKey; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div data-panel-field={field}>
      <h3 className={FIELD_HEAD_CLASS}>{t(`screenDetail.panel.${field}`)}</h3>
      {children}
    </div>
  );
}

/** 値の無い項目の「—」。 @graph-connects none */
function Dash() {
  return <span className="text-sm text-[var(--color-text-muted)]">{DASH}</span>;
}

/** `ScreenComponentPanel` の props。 @graph-connects none */
interface ScreenComponentPanelProps {
  component: ScreenComponentRow;
  /** リンクへ引き継ぐ案件番号。案件を選んでいなければ undefined（書き忘れを型で落とすため省略させない）。 */
  pj: string | undefined;
  onClose: () => void;
}

/**
 * コンポーネント 1 件の右パネル（案件なし、または案件で何も変わっていない画面）。
 *
 * 種別・表示条件・変数部の表示ロジック・操作結果を上から全部並べる。表示条件は業務ルールへのリンク
 * （無ければ「常に表示」）、変数部の表示ロジックは用語の詳細へのリンクと定義の 1 行目、操作結果は
 * 「「〈業務名〉」を実行する」（業務 ・ 業務ルールへのリンク）と「「〈画面名〉」に遷移する」（画面詳細へのリンク）
 * にする。案件を選んでいればリンクは案件を持ったまま繋ぐ — 落とすと、押した先だけ案件の外れた姿が
 * 開き、落ちたことは画面に出ない。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネント 1 件の種別・表示条件（業務ルールへのリンクか常に表示）・変数部の表示ロジック（用語へのリンクと定義）・操作結果（業務 ・ 業務ルール ・ 画面へのリンク）を右パネルに並べ、見出しのコンポーネント名の用語と定義の中の用語を用語詳細へ繋ぎ、リンクは案件を引き継ぐ。
 * @graph-connects ComponentName [calls] 見出しのコンポーネント名（用語は用語詳細へのリンク）
 * @graph-connects TermText [calls] 定義の 1 行目の中の用語リンク
 * @graph-connects componentKindText [calls] 種別
 * @graph-connects displayLabel [calls] 業務ルール ・ 用語の表示名
 * @graph-connects operationResultsOf [calls] 操作結果の行
 * @graph-connects OperationResultLink [calls] 操作結果 1 行のリンク
 * @graph-connects PanelItem [calls] 項目 1 つ
 * @graph-connects firstLineOf [calls] 用語の定義の 1 行目
 * @graph-connects pjLinkSearch [calls] 案件を次の画面へ引き継ぐ検索条件
 * @graph-connects TERM_DETAIL_ROUTE [references] 変数部の表示ロジックの用語詳細
 * @graph-connects RULE_ROUTE [references] 表示条件の業務ルール
 */
export function ScreenComponentPanel({ component, pj, onClose }: ScreenComponentPanelProps) {
  const { t } = useTranslation();
  const kind = componentKindText(component, t);
  const subject = component.subject ?? null;
  const { visibleWhen } = component;
  const results = operationResultsOf(component, t);

  return (
    <PanelFrame title={<ComponentName component={component} link />} onClose={onClose}>
      <PanelItem field="kind">
        {kind === null ? <Dash /> : <p className="text-sm">{kind}</p>}
      </PanelItem>
      <PanelItem field="visibleWhen">
        {visibleWhen === null ? (
          <p className="text-sm">{t('screenDetail.panel.alwaysVisible')}</p>
        ) : (
          <Link
            to={RULE_ROUTE}
            params={{ ruleId: String(visibleWhen.id) }}
            {...pjLinkSearch(pj)}
            className={`${LINK_CLASS} text-sm`}
          >
            {displayLabel(visibleWhen.label, t)}
          </Link>
        )}
      </PanelItem>
      <PanelItem field="variableLogic">
        {subject === null ? (
          <Dash />
        ) : (
          <div className="text-sm">
            <Link
              to={TERM_DETAIL_ROUTE}
              params={{ termId: String(subject.id) }}
              {...pjLinkSearch(pj)}
              className={LINK_CLASS}
            >
              {displayLabel(subject.label, t)}
            </Link>
            {subject.description !== null && subject.description !== '' && (
              <p
                data-subject-description
                className="mt-1 text-xs text-[var(--color-text-secondary)]"
              >
                <TermText text={firstLineOf(subject.description)} />
              </p>
            )}
          </div>
        )}
      </PanelItem>
      <PanelItem field="result">
        {results.length === 0 ? (
          <Dash />
        ) : (
          <ul>
            {results.map((result) => (
              <li key={result.key} className={ITEM_CLASS}>
                <OperationResultLink result={result} pj={pj} />
              </li>
            ))}
          </ul>
        )}
      </PanelItem>
    </PanelFrame>
  );
}

/** 2 列の片側のセルの class。 @graph-connects none */
const SIDE_CELL_CLASS = 'px-2 py-1.5 text-sm whitespace-pre-line';

/** 片側に無いコンポーネントの空札。斜線の地は線の色のトークンで描く。 @graph-connects none */
const ABSENT_CLASS =
  'min-h-7 bg-[repeating-linear-gradient(135deg,transparent_0_6px,var(--color-border)_6px_7px)]';

/** `ScreenComponentComparePanel` の props。 @graph-connects none */
interface ScreenComponentComparePanelProps {
  /** 変更前のコンポーネント。案件が足したコンポーネントなら null。 */
  before: ScreenComponentRow | null;
  /** 変更後のコンポーネント。案件が消したコンポーネントなら null。 */
  after: ScreenComponentRow | null;
  /** 見出し（コンポーネント名。追加 ・ 変更 ・ 削除は指すと出す）。 */
  title: ReactNode;
  onClose: () => void;
}

/**
 * コンポーネント 1 件の右パネル（案件モード）。項目ごとに 変更前 ／ 変更後 の 2 列にする。
 *
 * 案件なしと同じ 4 項目を同じ順で並べる。変わった項目は変更前を赤、変更後を緑の地色、変わって
 * いない項目は両側を薄い字、コンポーネントが片側に無ければその側を斜線の空欄にする（案件モードの
 * 詳細の表と同じ）。「変更前 公開中の仕様」「変更後」「（無し）」は列の並びと色で分かるので、セルを
 * 指すと出す文字にだけ載せる。案件モードでは画面を移るリンクを置かない（左右の片方にしか無い遷移先への
 * リンクは、開いた先の案件の姿と食い違う）。本文の中の用語だけはリンクにする — 辞書は案件を重ねた
 * 用語集なので、案件の姿に居ない用語（案件が消した用語）はリンクにならず、食い違いは起きない。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードでコンポーネント 1 件の種別・表示条件・変数部の表示ロジック・操作結果を、変更前と変更後の 2 列で右パネルに並べ、本文の用語は用語詳細へ繋ぐ。
 * @graph-connects componentPanelLines [calls] 項目ごとの文字
 * @graph-connects PanelItem [calls] 項目 1 つ
 * @graph-connects CompareSide [calls] 2 列の片側
 */
export function ScreenComponentComparePanel({
  before,
  after,
  title,
  onClose,
}: ScreenComponentComparePanelProps) {
  const { t } = useTranslation();
  const beforeLines = before === null ? null : componentPanelLines(before, t);
  const afterLines = after === null ? null : componentPanelLines(after, t);

  return (
    <PanelFrame title={title} onClose={onClose}>
      {PANEL_FIELDS.map((field) => {
        const left = beforeLines?.[field] ?? null;
        const right = afterLines?.[field] ?? null;
        return (
          <PanelItem key={field} field={field}>
            <div className="grid grid-cols-2 gap-1">
              <CompareSide side="before" value={left} other={right} />
              <CompareSide side="after" value={right} other={left} />
            </div>
          </PanelItem>
        );
      })}
    </PanelFrame>
  );
}

/** `CompareSide` の props。 @graph-connects none */
interface CompareSideProps {
  side: 'before' | 'after';
  /** この側の値。コンポーネントがこの側に無ければ null。 */
  value: string | null;
  /** 反対側の値。 */
  other: string | null;
}

/**
 * 2 列の片側 1 つ。コンポーネントがこの側に無ければ斜線の空欄（指すと「（無し）」）、反対側と同じなら薄い字、
 * 違えば地色。どちらの側かは指すと出す（「変更前 公開中の仕様」「変更後」）。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 右パネルの 2 列の片側を、無い・同じ・変わったに応じた空欄・薄い字・地色で描き、本文の用語は用語詳細へ繋ぎ、側の名前と「（無し）」は指すと出す。
 * @graph-connects TermText [calls] 本文の用語リンク
 */
function CompareSide({ side, value, other }: CompareSideProps) {
  const { t } = useTranslation();
  const sideLabel =
    side === 'before'
      ? `${t('specDiff.paneBefore')} ${t('specDiff.paneBeforeNote')}`
      : t('specDiff.paneAfter');
  if (value === null) {
    return (
      <div
        data-side={side}
        data-absent="true"
        title={`${sideLabel}: ${t('pjCompare.absent')}`}
        className={`${SIDE_CELL_CLASS} ${ABSENT_CLASS}`}
      />
    );
  }
  const same = value === other;
  const tone = same
    ? 'bg-[var(--color-bg-base)] text-[var(--color-text-muted)]'
    : side === 'before'
      ? 'bg-[var(--color-diff-removed-cell)]'
      : 'bg-[var(--color-diff-added-cell)]';
  return (
    <div
      data-side={side}
      data-same={same}
      title={sideLabel}
      className={`${SIDE_CELL_CLASS} ${tone}`}
    >
      <TermText text={value} />
    </div>
  );
}
